import type { ReactNode } from 'react';
import { Star, Trash2 } from 'lucide-react';
import { CONDITION_ONSET, type ConditionOnset } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { Badge, Button, Switch } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DIAGNOSIS_CERTAINTY, certaintyLabel, onsetLabel, type DiagnosisCertainty } from './labels';

export interface DraftDiagnosis {
  icdCode: string;
  icdTitle: string;
  isPrimary: boolean;
  certainty: DiagnosisCertainty;
  conditionOnset?: ConditionOnset;
  addToProblemList?: boolean;
}

const CERTAINTY_OPTIONS = () => DIAGNOSIS_CERTAINTY.map((value) => ({ value, label: certaintyLabel(value) }));
const ONSET_OPTIONS = () => CONDITION_ONSET.map((value) => ({ value, label: onsetLabel(value) }));

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-[6rem_minmax(0,1fr)] sm:items-center">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export function DiagnosisCard({
  diagnosis,
  readOnly,
  onChange,
  onPrimary,
  onRemove,
}: {
  diagnosis: DraftDiagnosis;
  readOnly: boolean;
  onChange: (patch: Partial<DraftDiagnosis>) => void;
  onPrimary: () => void;
  onRemove: () => void;
}) {
  return (
    <li
      className={cn(
        'space-y-3 rounded-xl border bg-card p-3 shadow-sm transition-colors sm:p-4',
        diagnosis.isPrimary && 'border-warning/40 bg-warning/[0.04]',
        diagnosis.certainty === 'ruled_out' && 'opacity-75',
      )}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          disabled={readOnly}
          aria-pressed={diagnosis.isPrimary}
          aria-label={tr('opd', 'dx_primary_toggle')}
          title={tr('opd', 'dx_primary_toggle')}
          onClick={onPrimary}
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition-colors focus-ring disabled:cursor-default',
            diagnosis.isPrimary ? 'border-warning/50 bg-warning/15 text-warning' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          <Star className={cn('h-4 w-4', diagnosis.isPrimary && 'fill-warning')} aria-hidden />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary/10 px-1.5 py-0.5 font-mono text-xs font-semibold text-primary">{diagnosis.icdCode}</span>
            {diagnosis.isPrimary && <Badge variant="warning">{tr('opd', 'dx_primary')}</Badge>}
          </div>
          <p className={cn('mt-1 text-sm font-semibold leading-snug', diagnosis.certainty === 'ruled_out' && 'line-through decoration-muted-foreground/60')}>{diagnosis.icdTitle}</p>
        </div>
        {!readOnly && (
          <Button variant="ghost" size="icon" aria-label={tr('opd', 'dx_remove_x', { code: diagnosis.icdCode })} onClick={onRemove}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        )}
      </div>

      <div className="space-y-2.5">
        <Row label={tr('opd', 'dx_certainty')}>
          <ChoiceButtons
            label={tr('opd', 'dx_certainty')}
            value={diagnosis.certainty}
            size="sm"
            disabled={readOnly}
            options={CERTAINTY_OPTIONS()}
            onChange={(next) => onChange({ certainty: next })}
          />
        </Row>
        <Row label={tr('opd', 'onset')}>
          <ChoiceButtons
            label={tr('opd', 'onset')}
            value={diagnosis.conditionOnset ?? null}
            size="sm"
            disabled={readOnly}
            options={ONSET_OPTIONS()}
            onChange={(next) => onChange({ conditionOnset: next })}
            onClear={() => onChange({ conditionOnset: undefined })}
          />
        </Row>
      </div>

      {!readOnly && (
        <Switch
          checked={Boolean(diagnosis.addToProblemList)}
          onCheckedChange={(checked) => onChange({ addToProblemList: checked })}
          label={tr('opd', 'dx_add_to_problem_list')}
          description={tr('opd', 'dx_add_to_problem_list_hint')}
        />
      )}
    </li>
  );
}
